import { CompanyNode, DepartmentNode, OrganizationTreeFilter } from 'features/common/models';
import _ from 'lodash';
import { DepartmentKey } from 'models';
import { createContext, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { findNode, findParentKeys, getDepartmentKey } from 'utils';
import organizationTreeService from '../../features/common/organizationTreeService';
import { DepartmentDatePicker, DepartmentMenu } from './components';
import './style.scss';

type DepartmentMenuContextType = {
  path: DepartmentKey[][];
  organizationData: CompanyNode[] | DepartmentNode[];
  activeDepartmentKeys?: DepartmentKey[];
  toggleSelectedDepartment: ((data: DepartmentNode[]) => void) | undefined;
};

export const DepartmentMenuContext = createContext<DepartmentMenuContextType>({
  path: [],
  organizationData: [],
  activeDepartmentKeys: undefined,
  toggleSelectedDepartment: undefined,
});

export interface SelectDepartmentModalProps {
  isModal?: boolean;
  defaultDataDate?: Date;
  showDataDatePicker?: boolean;
  defaultActiveDepartmentKeys?: DepartmentKey[];
  onClose?: () => void;
  onReflect?: (department: DepartmentNode[]) => void;
}

function getInitPathMenus(organizationData: CompanyNode[] | DepartmentNode[], departmentKeys: DepartmentKey[] = []) {
  let initPathMenus: DepartmentKey[][] = [];

  departmentKeys.forEach((departmentKey) => {
    const pathKeys = findParentKeys(organizationData, departmentKey.id, departmentKey.cd);
    if (pathKeys) {
      pathKeys.push(departmentKey);
      initPathMenus.push(pathKeys);
    }
  });

  return initPathMenus;
}

function SelectDepartmentModal({
  isModal = false,
  defaultDataDate = new Date(),
  showDataDatePicker = true,
  defaultActiveDepartmentKeys = [],
  onClose,
  onReflect,
}: SelectDepartmentModalProps) {
  const { t } = useTranslation();

  //state
  const [organizationData, setOrganizationData] = useState<CompanyNode[] | DepartmentNode[]>([]);
  const [activeDepartmentKeys, setActiveDepartmentKeys] = useState<DepartmentKey[]>(defaultActiveDepartmentKeys);

  const [filter, setFilter] = useState<OrganizationTreeFilter>({
    dataDate: defaultDataDate,
  });
  const initPathMenus = getInitPathMenus(organizationData, defaultActiveDepartmentKeys);
  const [pathMenus, setPathMenus] = useState<DepartmentKey[][]>(initPathMenus);
  const [selectedDepartments, setSelectedDepartments] = useState<DepartmentNode[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await organizationTreeService.getAll(filter).then((response) => {
        setOrganizationData(response.data);
        setLoading(false);
      });
    })();
  }, [filter]);

  useEffect(() => {
    handleDateChanged(defaultDataDate);
    setPathMenus([]);
    setActiveDepartmentKeys([]);
    setSelectedDepartments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDataDate]);

  useEffect(() => {
    if (organizationData.length > 0 && activeDepartmentKeys.length > 0) {
      const initPathMenus = getInitPathMenus(organizationData, activeDepartmentKeys);
      setPathMenus(initPathMenus);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData, activeDepartmentKeys]);

  useEffect(() => {
    const departmentNodes: DepartmentNode[] = [];
    activeDepartmentKeys.forEach((activeDepartmentKey) => {
      const departmentNode = findNode(organizationData, activeDepartmentKey.id, activeDepartmentKey.cd);
      if (departmentNode) {
        departmentNodes.push(departmentNode as DepartmentNode);
      }
    });
    setSelectedDepartments(departmentNodes);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData]);

  function handleClose() {
    onClose && onClose();
  }

  function handleDateChanged(date: Date) {
    const newFilter: OrganizationTreeFilter = {
      ...filter,
      dataDate: date,
    };
    setFilter(newFilter);
  }

  function handleToggleSelectedDepartment(departments: DepartmentNode[]) {
    let newPathMenus = [...pathMenus];
    let newSelectedDepartments = [...selectedDepartments];
    let newActiveDepartmentKeys = [...activeDepartmentKeys];

    departments.forEach((department) => {
      const departmentKey = getDepartmentKey(department);
      const parentMenuKeys = findParentKeys(organizationData, departmentKey.id, departmentKey.cd);

      const pathMenuIndex = pathMenus.findIndex((pathMenu) => {
        return (
          pathMenu.length > 0 &&
          pathMenu[pathMenu.length - 1].id === departmentKey.id &&
          pathMenu[pathMenu.length - 1].cd === departmentKey.cd
        );
      });

      if (pathMenuIndex < 0) {
        //add
        if (parentMenuKeys) {
          parentMenuKeys.push(departmentKey);
          newPathMenus.push(parentMenuKeys);
        } else {
          newPathMenus.push([departmentKey]);
        }

        newSelectedDepartments.push(department);
        newActiveDepartmentKeys.push(departmentKey);
      } else {
        //remove
        newPathMenus = newPathMenus.filter((pathMenu) => {
          return (
            pathMenu.length > 0 &&
            pathMenu[pathMenu.length - 1].id !== departmentKey.id &&
            pathMenu[pathMenu.length - 1].cd !== departmentKey.cd
          );
        });

        newSelectedDepartments = newSelectedDepartments.filter((selectedDepartment) => {
          return !_.isEqual(selectedDepartment, department);
        });

        newActiveDepartmentKeys = newActiveDepartmentKeys.filter(
          (activeDepartmentKey) =>
            activeDepartmentKey.id !== departmentKey.id || activeDepartmentKey.cd !== departmentKey.cd
        );
      }
    });

    setPathMenus(newPathMenus);
    setSelectedDepartments(newSelectedDepartments);
    setActiveDepartmentKeys(newActiveDepartmentKeys);
    !isModal && onReflect && onReflect(newSelectedDepartments);
  }

  function handleReflect() {
    onReflect && onReflect(selectedDepartments);
    isModal && handleClose();
  }

  const modalBody = (
    <div className="branch-menu">
      {showDataDatePicker && (
        <div className="mb-3">
          <Row>
            <Col sm={3}>
              <Form.Label className="col-form-label">{t('SelectDepartmentModal.target_date')}：</Form.Label>
            </Col>
            <Col sm={9}>
              <DepartmentDatePicker defaultDate={filter.dataDate} onDateChanged={handleDateChanged} />
            </Col>
          </Row>
        </div>
      )}
      {isModal && (
        <div className="text-end mb-2">
          <Button className="btn--dark-green" onClick={handleReflect}>
            {t('SelectDepartmentModal.selection')}
          </Button>
        </div>
      )}
      <div className="mb-2">
        <strong>{t('SelectDepartmentModal.target_store')}</strong>
      </div>
      {!loading ? (
        <DepartmentMenuContext.Provider
          value={{
            path: pathMenus,
            organizationData: organizationData,
            activeDepartmentKeys: activeDepartmentKeys,
            toggleSelectedDepartment: handleToggleSelectedDepartment,
          }}
        >
          <div className="department-menu-wrapper" style={{ minHeight: '100px', maxHeight: '480px' }}>
            {organizationData.length > 0 && <DepartmentMenu data={organizationData} />}
            {organizationData.length === 0 && <span>{t('SelectDepartmentModal.no_data_found')}</span>}
          </div>
        </DepartmentMenuContext.Provider>
      ) : (
        <div className="department-menu-wrapper" style={{ minHeight: '100px', maxHeight: '480px' }}>
          <span>{t('App.loading')}</span>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <Modal size="sm" show backdrop="static" onHide={handleClose} centered>
        <Modal.Header closeButton>
          <div className="page-head-title bg-violet p-2 fw-bold" style={{ minWidth: '150px' }}>
            {t('SelectDepartmentModal.head_title')}
          </div>
        </Modal.Header>
        <Modal.Body>{modalBody}</Modal.Body>
      </Modal>
    );
  }

  return modalBody;
}

export default SelectDepartmentModal;
